import { makeAutoObservable, runInAction, when } from "mobx";
import RootStore from "stores/RootStore";
import { mmErrorMessage } from "./mmErrorMessage";

// ============================================================================
// TYPES
// ============================================================================

export type MMContactRole =
  | "billing"
  | "project_manager"
  | "job_site"
  | "other";

export interface MMContact {
  id: string;
  companyId?: string;
  name: string;
  email: string;
  phone: string;
  roles: MMContactRole[];
}

export interface MMCompany {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
  phone: string;
  fax?: string;
  contacts: MMContact[];
}

const VALID_ROLES: MMContactRole[] = [
  "billing",
  "project_manager",
  "job_site",
  "other",
];

// Normalize any stored role value into a deduped MMContactRole[]. The backend
// `mm-contacts.role` column is a single NVARCHAR, so multiple roles are stored
// comma-joined (e.g. "billing,project_manager"). Accepts an array, a
// comma-separated string, or a single value; falls back to ["other"].
const normalizeRoles = (value: unknown): MMContactRole[] => {
  const raw: unknown[] = Array.isArray(value)
    ? value
    : typeof value === "string"
    ? value.split(",")
    : value != null
    ? [value]
    : [];
  const roles = raw
    .map((r) => (typeof r === "string" ? r.trim() : r))
    .filter((r): r is MMContactRole =>
      VALID_ROLES.includes(r as MMContactRole)
    );
  return roles.length > 0 ? Array.from(new Set(roles)) : ["other"];
};

// Join roles into the single comma-separated string the backend `role` column
// stores. Empty selection defaults to "other" (the column is NOT NULL).
const serializeRoles = (roles: MMContactRole[]): string =>
  roles.length > 0 ? Array.from(new Set(roles)).join(",") : "other";

// ============================================================================
// STORE
// ============================================================================

class MMCompanyStore {
  companies: MMCompany[] = [];
  selectedCompanyId: string | null = null;
  searchTerm: string = "";
  isLoading: boolean = false;
  error: string | null = null;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeAutoObservable(this, {
      rootStore: false,
    });
    this.rootStore = rootStore;

    // Auto-load when authenticated
    when(
      () => !!(this.rootStore.authStore as any)?.isSignedIn,
      () => this.loadData()
    );
  }

  // ========================================
  // SERVICE HELPERS
  // ========================================

  private get companiesService() {
    return this.rootStore.app.service("mm-companies");
  }

  private get contactsService() {
    return this.rootStore.app.service("mm-contacts");
  }

  // ========================================
  // DATA LOADING
  // ========================================

  loadData = async () => {
    if (this.isLoading) return;

    runInAction(() => {
      this.isLoading = true;
      this.error = null;
    });

    try {
      const [companiesResult, contactsResult] = await Promise.all([
        this.companiesService.find({ query: { $limit: 1000 } }),
        this.contactsService.find({ query: { $limit: 5000 } }),
      ]);

      const companiesData: any[] = Array.isArray(companiesResult)
        ? companiesResult
        : (companiesResult as any).data || [];
      const contactsData: any[] = Array.isArray(contactsResult)
        ? contactsResult
        : (contactsResult as any).data || [];

      // Group contacts by companyId
      const contactsByCompany = new Map<string, MMContact[]>();
      contactsData.forEach((c: any) => {
        const contact: MMContact = {
          id: c.contactId,
          companyId: c.companyId,
          name: c.name,
          email: c.email || "",
          phone: c.phone || "",
          roles: normalizeRoles(c.role ?? c.roles),
        };
        if (!contactsByCompany.has(c.companyId)) {
          contactsByCompany.set(c.companyId, []);
        }
        contactsByCompany.get(c.companyId)!.push(contact);
      });

      // Map companies with nested contacts
      const companies: MMCompany[] = companiesData.map((c: any) => ({
        id: c.companyId,
        name: c.name,
        address: c.address || "",
        city: c.city || "",
        state: c.state || "",
        zip: c.zip || "",
        phone: c.phone || "",
        fax: c.fax,
        contacts: contactsByCompany.get(c.companyId) || [],
      }));

      runInAction(() => {
        this.companies = companies;
        this.isLoading = false;
      });
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
        this.error =
          error instanceof Error ? error.message : "Failed to load companies";
      });
      console.error("Failed to load companies:", error);
    }
  };

  // ========================================
  // COMPUTED
  // ========================================

  get filteredCompanies(): MMCompany[] {
    if (!this.searchTerm) return this.companies;
    const term = this.searchTerm.toLowerCase();
    return this.companies.filter(
      (c) =>
        c.name.toLowerCase().includes(term) ||
        c.city.toLowerCase().includes(term) ||
        c.state.toLowerCase().includes(term)
    );
  }

  get selectedCompany(): MMCompany | null {
    if (!this.selectedCompanyId) return null;
    return this.companies.find((c) => c.id === this.selectedCompanyId) || null;
  }

  getCompanyById = (id: string): MMCompany | undefined => {
    return this.companies.find((c) => c.id === id);
  };

  getContactById = (
    companyId: string,
    contactId: string
  ): MMContact | undefined => {
    const company = this.getCompanyById(companyId);
    return company?.contacts.find((c) => c.id === contactId);
  };

  getContactsByRole = (
    companyId: string,
    role: MMContactRole
  ): MMContact[] => {
    const company = this.getCompanyById(companyId);
    return company?.contacts.filter((c) => c.roles.includes(role)) || [];
  };

  // ========================================
  // ACTIONS
  // ========================================

  setSearchTerm = (term: string) => {
    this.searchTerm = term;
  };

  setSelectedCompanyId = (id: string | null) => {
    this.selectedCompanyId = id;
  };

  createCompany = async (
    data: Omit<MMCompany, "id" | "contacts">
  ): Promise<MMCompany> => {
    try {
      const result: any = await this.companiesService.create({
        name: data.name,
        address: data.address,
        city: data.city,
        state: data.state,
        zip: data.zip,
        phone: data.phone,
        fax: data.fax,
      });

      const newCompany: MMCompany = {
        id: result.companyId,
        name: result.name,
        address: result.address || "",
        city: result.city || "",
        state: result.state || "",
        zip: result.zip || "",
        phone: result.phone || "",
        fax: result.fax,
        contacts: [],
      };

      runInAction(() => {
        this.companies.push(newCompany);
      });
      return newCompany;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to create company";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to create company"),
        options: { variant: "error" },
      });
      throw error;
    }
  };

  updateCompany = async (
    id: string,
    data: Partial<Omit<MMCompany, "id" | "contacts">>
  ): Promise<boolean> => {
    try {
      await this.companiesService.patch(id, data);

      runInAction(() => {
        const index = this.companies.findIndex((c) => c.id === id);
        if (index !== -1) {
          this.companies[index] = { ...this.companies[index], ...data };
        }
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to update company";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update company"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  deleteCompany = async (id: string): Promise<boolean> => {
    try {
      await this.companiesService.remove(id);

      runInAction(() => {
        const index = this.companies.findIndex((c) => c.id === id);
        if (index !== -1) {
          this.companies.splice(index, 1);
        }
        if (this.selectedCompanyId === id) {
          this.selectedCompanyId = null;
        }
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to delete company";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to delete company"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  // ========================================
  // CONTACT ACTIONS
  // ========================================

  addContact = async (
    companyId: string,
    data: Omit<MMContact, "id">
  ): Promise<MMContact | null> => {
    const company = this.getCompanyById(companyId);
    if (!company) return null;

    try {
      const result: any = await this.contactsService.create({
        companyId,
        name: data.name,
        email: data.email,
        phone: data.phone,
        // Backend column is a single `role` string; store multiple roles
        // comma-joined. Sending `roles` would be rejected in production
        // (additionalProperties: false), which silently dropped the contact.
        role: serializeRoles(data.roles),
      });

      const newContact: MMContact = {
        id: result.contactId,
        companyId,
        name: result.name,
        email: result.email || "",
        phone: result.phone || "",
        roles: normalizeRoles(result.role ?? result.roles),
      };

      runInAction(() => {
        company.contacts.push(newContact);
      });
      return newContact;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error ? error.message : "Failed to add contact";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to add contact"),
        options: { variant: "error" },
      });
      return null;
    }
  };

  updateContact = async (
    companyId: string,
    contactId: string,
    data: Partial<Omit<MMContact, "id">>
  ): Promise<boolean> => {
    const company = this.getCompanyById(companyId);
    if (!company) return false;

    try {
      // Convert the `roles` array to the backend's single comma-joined `role`
      // column; `roles` itself would be rejected in production.
      const { roles, ...rest } = data;
      const patchData: Record<string, unknown> = { ...rest };
      if (roles) {
        patchData.role = serializeRoles(roles);
      }

      await this.contactsService.patch(contactId, patchData);

      runInAction(() => {
        const contactIndex = company.contacts.findIndex(
          (c) => c.id === contactId
        );
        if (contactIndex !== -1) {
          company.contacts[contactIndex] = {
            ...company.contacts[contactIndex],
            ...data,
          };
        }
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to update contact";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to update contact"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  deleteContact = async (
    companyId: string,
    contactId: string
  ): Promise<boolean> => {
    const company = this.getCompanyById(companyId);
    if (!company) return false;

    try {
      await this.contactsService.remove(contactId);

      runInAction(() => {
        const contactIndex = company.contacts.findIndex(
          (c) => c.id === contactId
        );
        if (contactIndex !== -1) {
          company.contacts.splice(contactIndex, 1);
        }
      });
      return true;
    } catch (error) {
      runInAction(() => {
        this.error =
          error instanceof Error
            ? error.message
            : "Failed to delete contact";
      });
      this.rootStore.notificationStore?.addNotification({
        message: mmErrorMessage(error, "Failed to delete contact"),
        options: { variant: "error" },
      });
      return false;
    }
  };

  // ========================================
  // RESET
  // ========================================

  resetToDefaults = () => {
    runInAction(() => {
      this.companies = [];
      this.selectedCompanyId = null;
      this.searchTerm = "";
      this.error = null;
    });
    this.loadData();
  };
}

export default MMCompanyStore;
